/**
 * Utility for handling URL query parameters securely.
 * Implements AC-001: urlParams Utility Design
 * Resolves FD-065: URL Pre-population Security
 */

/**
 * List of keys that are reserved by the system and should not be 
 * allowed for user-defined pre-population.
 * Adheres to CP-002: Security & Blacklisting
 */
const SYSTEM_RESERVED_KEYS = Object.freeze([
  'redirect',
  'redirecturl',
  'sessionid',
  'session_id',
  'token',
  'auth',
  'api_base_url',
  'form_id',
  'trxref',
  'reference',
  'prefilled'
]);

/**
 * Extracts and filters query parameters from a search string.
 * @param searchString The URL search string (e.g., window.location.search)
 * @returns A sanitized Record of key-value pairs
 * 
 * Implements AC-001: getFilteredParams implementation
 */
export const getFilteredParams = (searchString: string): Record<string, string> => {
  const params: Record<string, string> = {};
  
  if (!searchString) return params;

  try {
    const searchParams = new URLSearchParams(searchString);

    searchParams.forEach((value, key) => {
      const normalizedKey = key.toLowerCase();
      
      // Check against blacklist (CP-002)
      if (!SYSTEM_RESERVED_KEYS.includes(normalizedKey)) {
        params[key] = value;
      }
    });
  } catch (error) {
    // Handle malformed search strings gracefully
    console.error('Error parsing search string:', error);
  }

  return params;
};

/**
 * Safely retrieves the query search string from:
 * 1. The iframe's own URL (window.location.search)
 * 2. The parent window's URL (if same-origin / accessible)
 * 3. The document referrer (fallback for cross-origin if parent page's Referrer Policy allows)
 */
export const getEmbedSearchString = (): string => {
  if (typeof window === 'undefined') return '';

  // 1. Prefer iframe's own query parameters if they exist
  if (window.location.search && window.location.search !== '?') {
    return window.location.search;
  }

  // 2. Try to read parent's location (succeeds if same-origin / same-site)
  if (window.parent && window.parent !== window) {
    try {
      if (window.parent.location.search) {
        return window.parent.location.search;
      }
    } catch (e) {
      // Cross-origin security error - ignore
    }
  }

  // 3. Fallback to document.referrer (succeeds cross-origin if Referrer Policy allows)
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      if (referrerUrl.search) {
        return referrerUrl.search;
      }
    } catch (e) {
      // Ignore invalid URLs
    }
  }

  return '';
};

